import {
  balanceFor,
  bucketFor,
  byCategory,
  monthPeriod,
  realMonthlyCost,
  realYearlyCost,
  yearPeriod,
} from '@apka/core'
import type { Bucket } from '@apka/core'
import { useOdhaczanie } from '../dane/odhaczanie.js'
import {
  uzyjKategorieWgId,
  uzyjPlatnosci,
  uzyjPozycjeWgId,
  uzyjWalutBezKursu,
  uzyjWidokowPlatnosci,
} from '../dane/widoki.js'
import { Licznik } from '../ui/Licznik.js'
import { ListaPlatnosci } from '../ui/ListaPlatnosci.js'
import {
  SzkieletLicznikow,
  SzkieletListy,
  SzkieletWykresuKategorii,
  SzkieletWykresuTrendu,
} from '../ui/Szkielet.js'
import { WykresKategorii } from '../ui/WykresKategorii.js'
import { WykresTrendu } from '../ui/WykresTrendu.js'
import { WyborMiesiaca, useMiesiac } from '../ui/WyborMiesiaca.js'
import { zl } from '../ui/format.js'
import { useUstawienieLogiczne } from '../ui/ustawienia.js'
import './pulpit.css'

const PUSTY: Bucket = { total: 0, paid: 0, pending: 0 }

export function Pulpit() {
  const { miesiac, przesun, doDzis, kierunek, odslona } = useMiesiac()
  const [rzeczywisty, ustawRzeczywisty] = useUstawienieLogiczne('rzeczywisty-koszt', false)

  const rok = yearPeriod(miesiac.rok)
  const okresMiesiaca = monthPeriod(miesiac.rok, miesiac.miesiac)
  // Jedno pobranie roku pokrywa wszystkie trzy liczniki oraz bilans miesiąca.
  const widoki = uzyjWidokowPlatnosci(rok.from, rok.to)
  const wczytane = widoki !== undefined
  const dane = widoki ?? []

  const miesieczny = wczytane
    ? bucketFor(dane, { cadence: 'monthly', type: 'expense', period: okresMiesiaca })
    : PUSTY
  const roczny = wczytane
    ? bucketFor(dane, { cadence: 'yearly', type: 'expense', period: rok })
    : PUSTY
  const jednorazowy = wczytane
    ? bucketFor(dane, { cadence: 'one_off', type: 'expense', period: rok })
    : PUSTY

  const miesiecznyRzecz = wczytane
    ? realMonthlyCost(dane, { year: miesiac.rok, month: miesiac.miesiac })
    : PUSTY
  const rocznyRzecz = wczytane ? realYearlyCost(dane, { year: miesiac.rok }) : PUSTY

  const bilans = wczytane ? balanceFor(dane, okresMiesiaca) : { income: 0, expense: 0, net: 0 }

  const platnosciMiesiaca = uzyjPlatnosci(okresMiesiaca.from, okresMiesiaca.to)
  const pozycjeWgId = uzyjPozycjeWgId()
  const kategorieWgId = uzyjKategorieWgId()
  const bezKursu = uzyjWalutBezKursu(rok.from, rok.to) ?? []
  const przelacz = useOdhaczanie()

  return (
    <>
      <header className="naglowek-ekranu">
        <div>
          <h1>Pulpit</h1>
          <p>Ile firma płaci i ile z tego już poszło</p>
        </div>
        <WyborMiesiaca miesiac={miesiac} onPrzesun={przesun} onDzis={doDzis} />
      </header>

      <div className="pulpit__tryb">
        <label className="przelacznik">
          <input
            type="checkbox"
            checked={rzeczywisty}
            onChange={(zdarzenie) => ustawRzeczywisty(zdarzenie.target.checked)}
          />
          <span className="przelacznik__tor" aria-hidden="true">
            <span className="przelacznik__suwak" />
          </span>
          <span className="przelacznik__opis">
            Rzeczywisty koszt
            <em>
              {rzeczywisty
                ? 'każdy licznik obejmuje wszystkie rytmy sprowadzone do swojego okresu'
                : 'każdy rytm liczony osobno, w swoim okresie'}
            </em>
          </span>
        </label>
      </div>

      {wczytane ? (
      <section
        className="pulpit__liczniki wjazd-listy"
        key={odslona}
        data-kierunek={kierunek}
      >
        <Licznik
          nazwa="Miesięcznie"
          opis={rzeczywisty ? 'z rocznymi ÷ 12' : 'koszty miesięczne'}
          kubelek={rzeczywisty ? miesiecznyRzecz : miesieczny}
          wyrozniony
        />
        <Licznik
          nazwa="Rocznie"
          opis={rzeczywisty ? 'z miesięcznymi × 12' : 'koszty roczne'}
          kubelek={rzeczywisty ? rocznyRzecz : roczny}
        />
        <Licznik nazwa="Jednorazowo" opis={`w roku ${miesiac.rok}`} kubelek={jednorazowy} />
      </section>
      ) : (
        <SzkieletLicznikow />
      )}

      {bezKursu.length > 0 ? (
        <p className="ostrzezenie-kursu" role="status">
          Brak kursu NBP dla {bezKursu.join(', ')} — te kwoty czekają na przeliczenie
          i na razie nie wchodzą do liczników.
        </p>
      ) : null}

      <section className="bilans karta wjazd-karty" aria-label="Bilans miesiąca">
        <div className="bilans__pozycja">
          <p className="bilans__etykieta">Wpływy</p>
          <p className="bilans__kwota bilans__kwota--przychod">{zl(bilans.income)}</p>
        </div>
        <div className="bilans__pozycja">
          <p className="bilans__etykieta">Wydatki</p>
          <p className="bilans__kwota">{zl(bilans.expense)}</p>
        </div>
        <div className="bilans__pozycja bilans__pozycja--wynik">
          <p className="bilans__etykieta">Wynik</p>
          <p
            className={
              bilans.net < 0
                ? 'bilans__kwota bilans__kwota--zalegle'
                : 'bilans__kwota bilans__kwota--przychod'
            }
          >
            {zl(bilans.net)}
          </p>
        </div>
      </section>

      <div className="wykresy">
        <section className="karta wjazd-karty" aria-labelledby="wykres-kategorii">
          <h2 className="naglowek-sekcji" id="wykres-kategorii">
            Wydatki miesiąca według kategorii
          </h2>
          {wczytane ? (
            <WykresKategorii
              udzialy={byCategory(dane, { type: 'expense', period: okresMiesiaca })}
              kategorie={kategorieWgId ?? new Map()}
            />
          ) : (
            <SzkieletWykresuKategorii />
          )}
        </section>

        <section className="karta wjazd-karty" aria-labelledby="wykres-trendu">
          <h2 className="naglowek-sekcji" id="wykres-trendu">
            Przebieg roku {miesiac.rok}
          </h2>
          {wczytane ? (
            <WykresTrendu widoki={dane} rok={miesiac.rok} wybranyMiesiac={miesiac.miesiac} />
          ) : (
            <SzkieletWykresuTrendu />
          )}
        </section>
      </div>

      <section aria-labelledby="platnosci-miesiaca">
        <h2 className="naglowek-sekcji" id="platnosci-miesiaca">
          Płatności w tym miesiącu
        </h2>
        {platnosciMiesiaca === undefined ? (
          <SzkieletListy />
        ) : (
          <ListaPlatnosci
            platnosci={platnosciMiesiaca}
            pozycje={pozycjeWgId ?? new Map()}
            kategorie={kategorieWgId ?? new Map()}
            onPrzelacz={(platnosc) => void przelacz(platnosc)}
            pustaTresc="Nic do zapłacenia w tym miesiącu."
          />
        )}
      </section>
    </>
  )
}
